import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company_name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await register(form);
      toast.success(`Account created. Welcome, ${u.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 flex" data-testid="register-page">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden border-r border-slate-200">
        <div className="absolute inset-0 sk-grid-bg opacity-40" />
        <div className="absolute inset-0 sk-radial-glow" />
        <div className="relative p-16 flex flex-col justify-between">
          <Logo size="lg" />
          <div>
            <h2 className="font-heading text-4xl font-semibold tracking-tight max-w-md">
              Apply once. Run on <span className="text-[#00D533]">175 banks</span>.
            </h2>
            <ul className="mt-8 space-y-3 text-sm text-slate-500">
              {[
                "Up to $25,000+ per transaction",
                "Keep your existing acquiring banks",
                "Per-transaction AML & risk treatment",
                "Chargeback guarantees on eligible disputes",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00D533]" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10"><Logo size="lg" /></div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight" data-testid="register-title">Create your merchant account</h1>
          <p className="mt-2 text-slate-500">Takes ~30 seconds. Underwriting follows.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-slate-600">Full name</Label>
              <Input id="name" required value={form.name} onChange={update("name")} className="mt-2 bg-[#FFFFFF] border-slate-200 h-11" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-600">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} className="mt-2 bg-[#FFFFFF] border-slate-200 h-11" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="company" className="text-slate-600">Company name (optional)</Label>
              <Input id="company" value={form.company_name} onChange={update("company_name")} className="mt-2 bg-[#FFFFFF] border-slate-200 h-11" data-testid="register-company-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-600">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={update("password")} className="mt-2 bg-[#FFFFFF] border-slate-200 h-11" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#00D533] hover:bg-[#00E636] text-black font-semibold" data-testid="register-submit-btn">
              {submitting ? "Creating..." : "Create account"} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs uppercase tracking-wider text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <Button onClick={handleGoogle} variant="ghost" className="w-full h-11 border border-slate-300 bg-slate-50 hover:bg-slate-100" data-testid="register-google-btn">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.5 29 4.5 24 4.5 13 4.5 4 13.5 4 24.5s9 20 20 20 20-9 20-20c0-1.4-.1-2.7-.4-4z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.5 29 4.5 24 4.5 16.4 4.5 9.8 8.7 6.3 14.7z"/><path fill="#4CAF50" d="M24 44.5c4.9 0 9.3-1.9 12.7-4.9l-5.9-5c-1.9 1.3-4.3 2-6.8 2-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 40.2 16.2 44.5 24 44.5z"/><path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l5.9 5c4.1-3.8 6.4-9.4 6.4-16.4 0-1.4-.1-2.7-.4-4z"/></svg>
            Continue with Google
          </Button>

          <p className="mt-8 text-center text-sm text-slate-500">
            Already onboarded?{" "}
            <Link to="/login" className="text-[#00D533] hover:underline" data-testid="register-login-link">
              Sign in instead
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
